import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  Collapse,
  TextField,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { surface } from '../../theme';
import { copyToClipboard } from './utils';
import MessageBubble from './MessageBubble';
import SleepVideoV3Service from '../../services/SleepVideoV3Service';

export default function ScriptSection({
  doc,
  videoTitle,
  videoContext,
  generating,
  generateError,
  onDismissGenerateError,
  scriptChip,
  generationStatus,
  progress,
  stepLabel,
  isGenerating,
  isCompleted,
  isFailed,
  canGenerate,
  messages,
  showFullScript,
  setShowFullScript,
  copiedFullScript,
  setCopiedFullScript,
  handleGenerate,
  generationDisabled = false,
  messagesEndRef,
  videoId,
  onScriptSaved,
  /** When set, called with trimmed script instead of SleepVideoV3Service.update */
  onSavePastedScript,
  /** Replaces default message when title/context prerequisites are not met */
  missingPrerequisitesMessage,
  /** Replaces default hint above Generate Script */
  generateScriptHint,
  /** Override visibility of the paste block (default: canGenerate || isCompleted) */
  showPasteSection,
}) {
  const chipLabel = scriptChip?.label ?? 'Idle';
  const chipColor = scriptChip?.color ?? 'default';

  const [pastedScript, setPastedScript] = useState('');
  const [savingScript, setSavingScript] = useState(false);
  const [saveScriptError, setSaveScriptError] = useState(null);

  useEffect(() => {
    setPastedScript(doc?.fullScript ?? '');
  }, [doc?.id]);

  const handleSavePastedScript = async () => {
    if (!videoId || typeof onScriptSaved !== 'function') return;
    setSaveScriptError(null);
    setSavingScript(true);
    try {
      const trimmed = pastedScript.trim();
      if (typeof onSavePastedScript === 'function') {
        await onSavePastedScript(trimmed);
      } else {
        await SleepVideoV3Service.update(videoId, { fullScript: trimmed });
      }
      setPastedScript(trimmed);
      onScriptSaved();
    } catch (err) {
      setSaveScriptError(err.response?.data?.error || err.message || 'Failed to save script');
    } finally {
      setSavingScript(false);
    }
  };

  const pasteVisible =
    showPasteSection !== undefined ? showPasteSection : canGenerate || isCompleted;
  const prereqMessage =
    missingPrerequisitesMessage ??
    'Video title and context are required. Edit this video from the list to set them.';
  const generateHint =
    generateScriptHint ?? 'Generate script using the saved video title and context.';

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        mb: 2,
      }}
    >
      <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
        Step 1: Script
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Chip
          label={chipLabel}
          color={chipColor}
          variant='outlined'
          size='small'
        />
      </Box>

      {(isGenerating || isCompleted) && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 600, flex: 1 }}>
              {isCompleted ? 'Generation Complete' : stepLabel}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {isFailed && generationStatus?.error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Generation failed: {generationStatus.error}
        </Alert>
      )}
      {generateError && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={onDismissGenerateError}>
          {generateError}
        </Alert>
      )}

      {canGenerate && (
        <Box sx={{ mb: 2 }}>
          {videoTitle.trim() && videoContext.trim() ? (
            <>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                {generateHint}
              </Typography>
              <Button
                variant='contained'
                size='medium'
                startIcon={
                  generating ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : isFailed ? (
                    <ReplayIcon />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={handleGenerate}
                disabled={generating || generationDisabled}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {generating ? 'Starting...' : isFailed ? 'Retry' : 'Generate Script'}
              </Button>
              {isFailed && doc?.debugScreenshotUrl && (
                <Button
                  variant='outlined'
                  size='medium'
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(doc.debugScreenshotUrl, '_blank', 'noopener,noreferrer')}
                  disabled={generating}
                  sx={{ ml: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  View Debug Screenshot
                </Button>
              )}
            </>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              {prereqMessage}
            </Typography>
          )}
        </Box>
      )}

      {videoId && onScriptSaved && pasteVisible && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {isCompleted ? 'Replace with pasted script' : 'Or paste your own script'}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={14}
            placeholder='Paste or type your script here…'
            value={pastedScript}
            onChange={(e) => setPastedScript(e.target.value)}
            disabled={isGenerating}
            sx={{ mb: 1.5 }}
            slotProps={{
              input: {
                sx: {
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  maxHeight: 320,
                  overflowY: 'auto',
                },
              },
            }}
          />
          {saveScriptError && (
            <Alert severity='error' sx={{ mb: 1.5 }} onClose={() => setSaveScriptError(null)}>
              {saveScriptError}
            </Alert>
          )}
          <Button
            variant='outlined'
            size='medium'
            startIcon={savingScript ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
            onClick={handleSavePastedScript}
            disabled={savingScript || isGenerating}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {savingScript ? 'Saving…' : 'Save script'}
          </Button>
        </Box>
      )}

      {messages.length > 0 && !isCompleted && (
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
            ChatGPT Conversation ({messages.length} message{messages.length !== 1 ? 's' : ''})
          </Typography>
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              px: 1,
              py: 1,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        </Box>
      )}

      {isCompleted && doc?.fullScript && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowFullScript(!showFullScript)}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600, flex: 1 }}>
              Full Script
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mr: 1 }}>
              {(doc.fullScript || '').trim().split(/\s+/).filter(Boolean).length} words
            </Typography>
            <Button
              size='small'
              variant='outlined'
              startIcon={copiedFullScript ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={async (e) => {
                e.stopPropagation();
                const ok = await copyToClipboard(doc.fullScript);
                if (ok) {
                  setCopiedFullScript(true);
                  setTimeout(() => setCopiedFullScript(false), 2000);
                }
              }}
            >
              {copiedFullScript ? 'Copied!' : 'Copy'}
            </Button>
            <IconButton size='small'>
              {showFullScript ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showFullScript}>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 1 }}>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {doc.fullScript}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}
